/*
 * Compact inline live-events panel. Drops onto any page that wants a
 * narrow stream of recent events filtered to one category / one action
 * prefix. The full Live monitor page (`/app/live`) uses the same hook
 * but renders a wider layout with counter table + filter pills.
 */

import { useState } from "react";
import {
  useLiveEvents,
  LIVE_CATEGORIES,
  LiveEventCategory,
  summarizeLiveDetail,
  fmtLiveTime
} from "../lib/useLiveEvents.js";

export interface LivePanelProps {
  title: string;
  /** Limit to one or more categories. */
  categories?: LiveEventCategory[];
  /** Only events whose action starts with this prefix. */
  actionPrefix?: string;
  /** Hard tail cap. Default 25 — small for an inline panel. */
  tailMax?: number;
  /** When true, renders the connection-state pill in the header. Default true. */
  showConnectionState?: boolean;
}

export default function LivePanel({
  title,
  categories,
  actionPrefix,
  tailMax = 25,
  showConnectionState = true
}: LivePanelProps) {
  const [paused, setPaused] = useState(false);
  const live = useLiveEvents({ categories, actionPrefix, tailMax, paused });

  return (
    <section className="card live-panel" style={{ marginTop: 16 }}>
      <header className="card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <strong>{title}</strong>
          {showConnectionState ? (
            <span className={`pill ${live.connectionState === "open" ? "pill-ok" : live.connectionState === "connecting" ? "pill-info" : "pill-err"}`}
                  style={{ fontSize: 10 }}>
              {live.connectionState === "open" ? "● Live" : live.connectionState === "connecting" ? "Connecting…" : "Off"}
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-sm" type="button" onClick={() => setPaused((p) => !p)}>
            {paused ? "Resume" : "Pause"}
          </button>
          <button className="btn btn-sm btn-secondary" type="button" onClick={live.clear}>Clear</button>
        </div>
      </header>
      <div className="card-body" style={{ padding: 0, maxHeight: 320, overflowY: "auto" }}>
        {live.events.length === 0 ? (
          <p className="muted" style={{ padding: 16, textAlign: "center", fontSize: 12 }}>
            {paused ? "Paused." : "Listening for live events…"}
          </p>
        ) : (
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Time</th>
                <th style={{ width: 90 }}>Category</th>
                <th>Action</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {live.events.map((e) => {
                const cat = LIVE_CATEGORIES.find((c) => c.key === e.category);
                return (
                  <tr key={e.id}>
                    <td className="soft" style={{ whiteSpace: "nowrap" }}>{fmtLiveTime(e.ts)}</td>
                    <td>
                      <span className="pill" style={{ background: cat?.color ?? "#475569", color: "#fff", fontSize: 10 }}>
                        {cat?.label ?? e.category}
                      </span>
                    </td>
                    <td><code style={{ fontSize: 11 }}>{e.action}</code></td>
                    <td className="soft">{summarizeLiveDetail(e.detail)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
